import React from 'react';
import EngagementStageBadge from '../workflow/EngagementStageBadge';
import { formatDateShort } from '../../utils/dateFormat';
import NowName from './NowName';
import { shortEngagementName } from '../workflow/CoverageDisplay';
import { serviceLabel } from '../../utils/engagementName';

/**
 * "Agreed Upon Procedures #2" plus, for services that allow several
 * engagements per company and FYE, the description headline underneath
 * (the API's serviceDescription — see utils/engagementName engagementTag).
 */
export const ServiceCell = ({ r, className = 'text-slate-600' }) => (
  <span className={className}>
    <span>{serviceLabel(r.serviceProvided, r.engagementSeq) || '—'}</span>
    {r.serviceDescription && (
      <span className="block max-w-[16rem] truncate text-xs text-slate-400" title={r.serviceDescription}>
        {r.serviceDescription}
      </span>
    )}
  </span>
);

/**
 * A date column cell: '30 Sep' and '2026' each unbreakable, so a narrow column
 * breaks once before the year and never stacks day / month / year on three
 * lines. Wide enough and it stays on one line.
 */
export const DateCell = ({ value }) => {
  const s = value ? formatDateShort(value) : '';
  if (!s) return <span className="text-slate-400">—</span>;
  const cut = s.lastIndexOf(' ');
  if (cut < 0) return <span className="whitespace-nowrap">{s}</span>;
  return (
    <>
      <span className="whitespace-nowrap">{s.slice(0, cut)}</span>{' '}
      <span className="whitespace-nowrap">{s.slice(cut + 1)}</span>
    </>
  );
};

// The ONE engagement list column set. Two surfaces render
// the same table — Management → Engagements (/qm/engagements, WorkflowListShell)
// and My Records → Engagements (/my?tab=engagements, embedded shell). They must
// describe an engagement identically, so the columns live here; the surfaces
// differ only in scope (all vs mine), row target and toolbar.
//
// Both endpoints go through engagementsRepository.list, so every field —
// including the derived currentStage — is present on either side.
const fmtDate = (v) => (v ? formatDateShort(v) : '—');

// No File Ref. column: the reference stays on the detail page and is still
// matched by the search box.
// showAudit=false drops Sign Off and Archival Deadline (My Records, viewer
// without an Audit key — canSeeAuditStages).
export const engagementListColumns = ({ showAudit = true } = {}) => [
  // Names are the engagement's own snapshots; a renamed client master shows
  // "(now: …)" after them (docs/data-model.md §2).
  {
    key: 'clientGroup',
    label: 'Client Group',
    sortKey: 'clientGroup',
    className: 'text-slate-700 min-w-[10rem]',
    render: (r) => <NowName value={r.clientGroup} now={r.clientGroupNow} className="text-slate-600" />,
  },
  {
    key: 'entityName',
    label: 'Entity Name',
    sortKey: 'entityName',
    className: 'text-slate-700 min-w-[14rem]',
    render: (r) => (r.entityName
      ? <NowName value={r.entityName} now={r.entityNameNow} />
      : <NowName value={r.engagementName} now={r.engagementNameNow ? shortEngagementName(r.engagementNameNow) : null} />),
  },
  { key: 'yearEnd', label: 'FYE', sortKey: 'yearEnd', render: (r) => <DateCell value={r.yearEnd} /> },
  // One company may hold several engagements for one FYE (one per service,
  // numbered when the service allows several) — without this column those
  // rows read identically.
  { key: 'service', label: 'Service', sortKey: 'serviceProvided', render: (r) => <ServiceCell r={r} /> },
  { key: 'partner', label: 'Partner', sortKey: 'partner', render: (r) => <NowName value={r.partner} now={r.partnerNow} className="text-slate-600" /> },
  // 在審時點得開「在等誰簽核」（共用同一張小卡）。
  { key: 'stage', label: 'Current Stage', render: (r) => (r.currentStage ? <EngagementStageBadge stage={r.currentStage} engagementId={r.id} /> : <span className="text-slate-400">—</span>) },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    render: (r) => (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
        {r.status}
      </span>
    ),
  },
  ...(showAudit ? [
    { key: 'signOffDate', label: 'Sign Off', sortKey: 'signOffDate', render: (r) => <span className="whitespace-nowrap text-slate-600">{fmtDate(r.signOffDate)}</span> },
    { key: 'archivalDeadline', label: 'Archival Deadline', sortKey: 'archivalDeadline', render: (r) => <span className="whitespace-nowrap text-slate-600">{fmtDate(r.archivalDeadline)}</span> },
  ] : []),
];

export default engagementListColumns;
